import { FC } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { CollectionsPageImage } from '@frontend/payments-assets';
import { StripeSignupModal } from '@frontend/payments-setup';
import { useBulkCollectionsUIShallowStore } from '@frontend/payments-state';
import { useNavigate } from '@frontend/weave-router';
import { PromoBannerBadge } from '../../../common/components/promo-banners/promo-banner-badge';
import { BULK_COLLECTIONS_ARTICLE_LINK, BulkCollectionsDescriptionTextMap } from '../../constants';
import { useBulkCollectionsBannerEligibility } from '../../hooks/useBulkCollectionsBannerEligibility';
import { BulkCollectionsPromoTrackingIds } from '../../tracking-ids';
import { PaymentsPromoBanner } from '../common/payments-promo-banner';

interface BulkCollectionsPromotionBannerProps {
  styles: Interpolation<Theme>;
  onClose?: () => void;
  innerRef?: React.LegacyRef<HTMLDivElement>;
}

const styleSheets = {
  featureImage: css`
    border: 1px solid var(--fabric-color-neutral-alpha-100-alpha-10);
    filter: drop-shadow(0px 4px 8px rgba(30, 50, 81, 0.15)) drop-shadow(0px 1px 3px rgba(30, 50, 81, 0.15));
    height: auto;
  `,
  positionAbs: css`
    position: absolute;
    right: 4%;
    top: 8%;
  `,
};

export const BulkCollectionsPromotionBanner: FC<BulkCollectionsPromotionBannerProps> = ({
  styles,
  innerRef,
  onClose,
}) => {
  const { t } = useTranslation('highValueAdoption', { keyPrefix: 'payments' });
  const navigate = useNavigate();
  const { setShouldShowSelectAllCallout } = useBulkCollectionsUIShallowStore('setShouldShowSelectAllCallout');
  const { pmsName, showBanner, bannerType, markDismissed, stripeSignupModalProps, primaryAction, isButtonLoading } =
    useBulkCollectionsBannerEligibility({
      navigateToCollections: () => {
        setShouldShowSelectAllCallout(true);
        navigate({
          to: '/payments/invoices',
          search: { status: 'collections' },
        });
      },
    });

  if (!showBanner) {
    return null;
  }

  return (
    <div ref={innerRef} css={styles}>
      <PaymentsPromoBanner
        headerText={t('Collect outstanding balances from {{pmsName}} all at once.', { pmsName })}
        HeaderPrefix={() => <PromoBannerBadge badgeType='new' />}
        titleText={t('Bulk Collections')}
        descriptionText={BulkCollectionsDescriptionTextMap(pmsName)[bannerType]}
        FeatureImage={() => (
          <CollectionsPageImage
            width={260}
            borderRadius='var(--fabric-geometry-compact-radius-lg)'
            css={[styleSheets.featureImage, styleSheets.positionAbs]}
          />
        )}
        showLoader={isButtonLoading}
        footerActions={{
          primary: primaryAction,
          secondary: {
            text: t('Learn More'),
            trackingId: `${BulkCollectionsPromoTrackingIds.learnMore}:${bannerType}`,
            onClick: () => {
              window.open(BULK_COLLECTIONS_ARTICLE_LINK, '_blank', 'noopener,noreferrer');
            },
          },
          tertiary: {
            text: t('Maybe Later'),
            trackingId: `${BulkCollectionsPromoTrackingIds.maybeLater}:${bannerType}`,
            onClick: () => {
              markDismissed();
              if (onClose) {
                onClose();
              }
            },
          },
        }}
      />
      <StripeSignupModal {...stripeSignupModalProps} />
    </div>
  );
};
